import { marketSessionDate } from 'utils/shared/time/marketSessionDate';
import {
  extendedCloseMinutes,
  marketSessionPhase,
  PRE_MARKET_OPEN_MINUTES,
  REGULAR_OPEN_MINUTES,
  type MarketSessionPhase,
} from 'utils/shared/time/marketSession';
import { usEquityCloseMinutes } from 'utils/shared/time/usEquityCalendar';

/**
 * Which page the disclosure is mounted on. Only the extra sales copy varies — the four feed rows
 * and their windows are identical everywhere, because they state when each feed DELIVERS, which is
 * a fact about the market rather than about any one screen. The one addition is the `gex` surface's
 * Open Interest row, because only the GEX pages show open interest.
 */
export type MarketDataSurface = 'flow' | 'research' | 'gex';

/**
 * The viewer's options-flow entitlement.
 *
 * `unknown` is not a cosmetic third state: entitlement resolves asynchronously, and every surface
 * in this app that collapsed it into "not entitled" ended up telling paying subscribers their tape
 * was delayed. While it is unknown the panel says nothing at all about a plan.
 */
export type FlowAccess = 'live' | 'delayed' | 'unknown';

/**
 * A named edge of the US-equity session, resolved to an ET minute-of-day for a specific date.
 *
 * The two OPENS are fixed by rule (4:00 AM pre-market, 9:30 AM bell). The two CLOSES move with the
 * exchange calendar: on a half-day the cash session ends at 1:00 PM instead of 4:00 PM and the tape
 * with it, so a row that says "runs to the close" has to mean THIS date's close, not a constant. The
 * closes are the one place the published early-close calendar (installed from `/v1/reference/calendar`)
 * changes what the panel shows.
 */
export type SessionBoundary = 'pre-open' | 'bell' | 'cash-close' | 'extended-close';

/** The boundary as ET minutes-of-day on `sessionDate` — half-day-aware for the two closes. */
export const boundaryMinutes = (boundary: SessionBoundary, sessionDate: string): number => {
  switch (boundary) {
    case 'pre-open':
      return PRE_MARKET_OPEN_MINUTES; // 4:00 AM ET
    case 'bell':
      return REGULAR_OPEN_MINUTES; // 9:30 AM ET
    case 'cash-close':
      return usEquityCloseMinutes(sessionDate); // 4:00 PM, or 1:00 PM on a half-day
    case 'extended-close':
      return extendedCloseMinutes(sessionDate); // 8:00 PM, or 5:00 PM on a half-day
  }
};

export interface FeedRow {
  feed: string;
  /** Where the window opens and closes, named so each close tracks the day's real session. */
  startsAt: SessionBoundary;
  endsAt: SessionBoundary;
  /** Sessions in which this feed is delivering. */
  openIn: readonly MarketSessionPhase[];
  /** Withheld entirely without a subscription, rather than delivered late. */
  subscriberOnly?: boolean;
}

const ALL_PRICE_SESSIONS = ['pre', 'regular', 'after'] as const;
const CASH_SESSION_ONLY = ['regular'] as const;

/**
 * The four rows, in the order a trader reads them: what moves all day, then what moves with the
 * bell.
 *
 * Dark Pool runs from the pre-market open to the extended close, not 8:00 AM–4:00 PM. Two production
 * trading days show prints from 04:00:00.6 to 20:00:07 ET, with 177 landing before 8:00 AM on
 * 2026-08-21 alone — the old 8:00 AM start advertised four hours less coverage than we deliver.
 *
 * Each close is a NAMED boundary rather than a fixed minute, so on an exchange half-day the windows
 * (and the lead sentence, which reads the same boundaries) shorten to the real 1:00 PM / 5:00 PM
 * close instead of overstating an 8:00 PM tape.
 */
export const FEED_ROWS: readonly FeedRow[] = [
  { feed: 'Prices', startsAt: 'pre-open', endsAt: 'extended-close', openIn: ALL_PRICE_SESSIONS },
  {
    feed: 'Dark Pool',
    startsAt: 'pre-open',
    endsAt: 'extended-close',
    openIn: ALL_PRICE_SESSIONS,
    subscriberOnly: true,
  },
  { feed: 'Options Flow', startsAt: 'bell', endsAt: 'cash-close', openIn: CASH_SESSION_ONLY },
  { feed: 'Gamma', startsAt: 'bell', endsAt: 'cash-close', openIn: CASH_SESSION_ONLY },
];

export const ET_ZONE = 'America/New_York';

/**
 * The window rendered in whichever timezone the viewer reads the rest of the app in.
 *
 * The boundaries are facts about the US session, so they are STORED in ET and CONVERTED for
 * display. A trader in London reading "9:30 AM to 4:00 PM" beside a clock showing 3pm has to do the
 * arithmetic themselves, and getting it wrong is the exact confusion this control exists to remove.
 *
 * A window that crosses local midnight is marked "+1" rather than silently reading backwards.
 */
const formatterCache = new Map<string, Intl.DateTimeFormat>();

const cachedFormatter = (key: string, build: () => Intl.DateTimeFormat): Intl.DateTimeFormat => {
  const cached = formatterCache.get(key);
  if (cached) return cached;
  const formatter = build();
  formatterCache.set(key, formatter);
  return formatter;
};

const clockIn = (zone: string) =>
  cachedFormatter(
    `clock:${zone}`,
    () =>
      new Intl.DateTimeFormat('en-US', {
        timeZone: zone,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }),
  );

const dayIn = (zone: string) =>
  cachedFormatter(`day:${zone}`, () => new Intl.DateTimeFormat('en-CA', { timeZone: zone }));

/** The instant at which `etMinutes` past midnight ET falls, on the session date containing `atMs`. */
const instantForEtMinutes = (atMs: number, etMinutes: number): number => {
  const [year, month, day] = marketSessionDate(atMs).split('-').map(Number);
  // The offset is measured at noon ET, which is never inside a DST transition.
  const noonGuess = Date.UTC(year, month - 1, day, 12);
  const etNoon = new Date(new Date(noonGuess).toLocaleString('en-US', { timeZone: ET_ZONE }));
  const utcNoon = new Date(new Date(noonGuess).toLocaleString('en-US', { timeZone: 'UTC' }));
  const offsetMinutes = Math.round((etNoon.getTime() - utcNoon.getTime()) / 60_000);
  return Date.UTC(year, month - 1, day, 0, etMinutes - offsetMinutes);
};

export const formatFeedWindow = (row: FeedRow, zone: string, atMs: number): string => {
  const sessionDate = marketSessionDate(atMs);
  const startAt = instantForEtMinutes(atMs, boundaryMinutes(row.startsAt, sessionDate));
  const endAt = instantForEtMinutes(atMs, boundaryMinutes(row.endsAt, sessionDate));
  const clock = clockIn(zone);
  const crossesMidnight = dayIn(zone).format(startAt) !== dayIn(zone).format(endAt);
  return `${clock.format(startAt)} – ${clock.format(endAt)}${crossesMidnight ? ' +1' : ''}`;
};

/**
 * A US-session boundary (ET minutes-of-day) written as a clock time in the viewer's own zone.
 *
 * The same conversion the feed-window column uses, so the prose sentence and the row times can never
 * disagree: "Prices are live until 8:00 PM" reads "6:00 PM" for a viewer in Denver, beside a Prices
 * row that also ends at 6:00 PM. An earlier version spelled these times as ET literals, which told
 * everyone outside Eastern the market ran hours later than it did.
 */
const clockLabelInZone = (etMinutes: number, zone: string, atMs: number): string =>
  clockIn(zone).format(instantForEtMinutes(atMs, etMinutes));

/** "Eastern" when the viewer reads the app in market time, "Local" otherwise. */
export const zoneLabel = (zone: string): string => (zone === ET_ZONE ? 'Eastern' : 'Local');

/** The row the tier badge and the upgrade line belong to. */
export const FLOW_ROW_FEED = 'Options Flow';

export const isFeedOpen = (row: FeedRow, phase: MarketSessionPhase): boolean =>
  row.openIn.includes(phase);

export const TRIGGER_LABEL: Record<MarketSessionPhase, string> = {
  regular: 'Markets open',
  pre: 'Pre-market',
  after: 'After-hours',
  closed: 'Markets closed',
};

/**
 * The label used before the clock has been read on the client.
 *
 * Three of the four host pages are pre-rendered, so this component's first paint ships inside HTML
 * that can be half an hour old. Reading a wall clock during render would bake a build-time answer
 * into that HTML and mismatch on hydration — and a mismatch this high in the tree discards the
 * whole SSR root. So the server renders a phase-free label and the real one arrives after mount.
 */
export const PENDING_LABEL = 'Market data';

export const PANEL_TITLE = 'Real-Time Market Data';

/**
 * The chip on a delivering row NAMES the session it is delivering in.
 *
 * An earlier draft said one word on every running row — "Live" — on the theory that a reader should
 * not have to learn a distinction that changes nothing they can do. But it does change something:
 * pre-market and after-hours prints are thinner and wider than the regular tape, a different quality
 * of data even while the feed is genuinely live. So the extended sessions name themselves ("Pre-
 * market" / "After-hours", in the extended-session purple), and only the regular session keeps the
 * plain, strongest-true word. The vocabulary matches the dot's own hover title, so the whole control
 * speaks one dialect. `closed` never renders — a closed row shows no chip at all.
 */
export const SESSION_CHIP: Record<MarketSessionPhase, string> = {
  regular: 'Live',
  pre: 'Pre-market',
  after: 'After-hours',
  closed: 'Live',
};

/** Delivered, but 30 minutes behind. */
export const DELAYED_CHIP = 'Delayed';

/** Not delivered at all without a subscription. */
export const LOCKED_CHIP = 'Locked';

/**
 * The lead sentence, which is the first thing read and therefore does the most selling.
 *
 * `weekday` is the next session's weekday, or null when the next open is later the same day. The
 * null case is load-bearing: between midnight and 4:00 AM on a trading day, coverage restarts in a
 * few hours — naming a weekday there would put the reader a full day out.
 *
 * The clock times are US-session facts stored in ET and converted into the viewer's `zone`, exactly
 * like the feed-window column below the sentence. Spelling them as ET literals told a reader in
 * Denver the tape ran until "8:00 PM" when their own clock said 6:00 PM — the precise confusion this
 * control exists to remove. The extended close is read from the session date's real close (see
 * `boundaryMinutes`), so on a half-day the sentence says 5:00 PM in step with the shortened rows.
 */
export const leadLine = (
  phase: MarketSessionPhase,
  weekday: string | null,
  zone: string,
  nowMs: number,
): string => {
  const sessionDate = marketSessionDate(nowMs);
  const preOpen = clockLabelInZone(PRE_MARKET_OPEN_MINUTES, zone, nowMs); // 4:00 AM ET
  const bell = clockLabelInZone(REGULAR_OPEN_MINUTES, zone, nowMs); // 9:30 AM ET
  const extendedClose = clockLabelInZone(
    boundaryMinutes('extended-close', sessionDate),
    zone,
    nowMs,
  );
  switch (phase) {
    case 'regular':
      return 'Every feed is live.';
    case 'pre':
      return `Prices are live. The options tape opens at ${bell}.`;
    case 'after':
      return weekday
        ? `Prices are live until ${extendedClose}. The tape reopens ${weekday} at ${bell}.`
        : `Prices are live until ${extendedClose}. The tape reopens at ${bell}.`;
    case 'closed':
    default:
      return weekday
        ? `Markets are closed. Coverage restarts ${weekday} at ${preOpen}.`
        : `Markets are closed. Coverage restarts at ${preOpen}.`;
  }
};

/**
 * The GEX page's headline claim, and the reason this component earns its place on that page.
 *
 * Every clause is measured. There is no cron on the serving path — the only GEX cron writes
 * end-of-day history and never touches what the page reads — so gamma is computed on demand from
 * the current chain, continuously, for every symbol. Sampled live in production on 2026-08-24,
 * SPY's payload stamp advanced on every sample and was never more than ~20s old, with cold tickers
 * returning as fresh as the hot list. "As fast as the market can be read" is the honest ceiling and
 * it is also the strongest thing we can say.
 */
export const GAMMA_HERO_HEADLINE = 'Gamma is live.';
export const GAMMA_HERO_BODY =
  'Every strike, every expiry, recomputed continuously. No batch window. No refresh schedule.';

/**
 * The open-interest sentence.
 *
 * A fact about the exchanges, never a limitation of ours: every gamma model on earth is built on
 * the same daily open interest, so saying it plainly costs nothing and pre-empts the "why didn't my
 * GEX move after that print?" question from a position of authority.
 */
export const OPEN_INTEREST_LINE =
  'Exchanges publish open interest once per day. Everything we build on it is live.';

/**
 * THE OPEN INTEREST ROW (tradedata#74), on the `gex` surface only, under Gamma.
 *
 * Exchanges send the day's open interest once, at 6:30 AM ET, and the GEX service serves it about a
 * minute later (see `OPEN_INTEREST_PUBLISH_MINUTES`). A 0DTE trader needs to know whether the OI on
 * screen is this morning's or yesterday's: on 2026-10-30 single SPX 0DTE strikes moved by 10,000
 * contracts overnight. The row answers that one question in the panel's existing row format:
 * "Today" when the OI on screen is today's, "Waiting" while a session runs on an older day's OI. It
 * names no pipeline time and no "as of" date; the time column states the exchanges' daily time, as
 * the other rows' windows state theirs.
 */
export const OPEN_INTEREST_ROW_FEED = 'Open Interest';
export const OPEN_INTEREST_CHIP = 'Today';
export const OPEN_INTEREST_WAITING_CHIP = 'Waiting';

/**
 * 6:30 AM ET: the time the exchange feed (OPRA) sends the day's open interest, which the row's time
 * column states. Every SPX and SPY open-interest message measured arrived between 6:30:00 and
 * 6:30:52 ET, on every day sampled and across both clock changes, so the time is fixed in ET
 * (Databento OPRA history, 2026-10-30).
 *
 * The row states the exchanges' time, not a later serving time (Trey, 2026-11-02). It replaced his
 * "Daily by 7:00 AM" of 2026-10-30, set when the server waited until 7:00 and then polled every 30
 * minutes. Since tradedata#75 the GEX service serves the release about a minute after 6:30, and on
 * a late day the chip reads "Waiting" until the day's OI is on screen.
 *
 * Trey (2026-11-02) put the time in the row only, not in the open-interest sentence under the rows:
 * a row without a time looks off beside the other rows' windows, because the row is the panel's
 * schedule column and the chip gives the live status.
 */
export const OPEN_INTEREST_PUBLISH_MINUTES = 6 * 60 + 30;

/** "Daily at 6:30 AM" in the viewer's own zone, converted exactly like the feed-window column. */
export const openInterestScheduleLabel = (zone: string, nowMs: number): string =>
  `Daily at ${clockLabelInZone(OPEN_INTEREST_PUBLISH_MINUTES, zone, nowMs)}`;

/** Hover text on the dot beside Total OI on the OI page. */
export const OPEN_INTEREST_DOT_TITLE = "Today's open interest";

/**
 * THE ONE RULE for both open-interest signals: the panel row and the dot beside the OI totals.
 *
 * On when the OI's session is today's ET date and a session is running. The colour is that
 * session's colour, so the marks always agree with the header dot. Closed (overnight, a weekend, a
 * holiday) is dark like every other row, even when the date matches. A missing or null date is dark:
 * the page makes no claim it cannot back.
 */
export const isTodaysOpenInterest = (
  oiSessionDate: string | null | undefined,
  phase: MarketSessionPhase,
  nowMs: number,
): boolean => phase !== 'closed' && !!oiSessionDate && oiSessionDate === marketSessionDate(nowMs);

/** A session date the GEX service stamps: `YYYY-MM-DD`. */
const SESSION_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The Open Interest row's chip: `today` with today's OI, `waiting` while a session runs on an older
 * day's OI, and null (no chip) when the market is closed or the date is missing, malformed or later
 * than today. A missing date is not "waiting": an origin that sends no date says nothing either way.
 */
export const openInterestRowState = (
  oiSessionDate: string | null | undefined,
  phase: MarketSessionPhase,
  nowMs: number,
): 'today' | 'waiting' | null => {
  if (isTodaysOpenInterest(oiSessionDate, phase, nowMs)) return 'today';
  if (phase === 'closed' || !oiSessionDate || !SESSION_DATE.test(oiSessionDate)) return null;
  // `YYYY-MM-DD` strings sort as dates.
  return oiSessionDate < marketSessionDate(nowMs) ? 'waiting' : null;
};

/** The rule read at one instant: the session whose colour lights the marks, or null for none. */
export const todaysOpenInterestPhase = (
  oiSessionDate: string | null | undefined,
  nowMs: number | null,
): MarketSessionPhase | null => {
  if (nowMs === null) return null;
  const phase = marketSessionPhase(nowMs);
  return isTodaysOpenInterest(oiSessionDate, phase, nowMs) ? phase : null;
};

/**
 * The money lines. Each one states the two gaps, then says what those gaps cost the reader.
 *
 * The second half is the half that converts, and it is written as a decision the trader cannot
 * make rather than a feature they do not have. A delayed tape is not a smaller tape — it is last
 * half hour's positioning, which is a different thing from this half hour's. Dark pool is not late,
 * it is absent: the page renders an "Unlock Dark + Lit Pool" wall, so a viewer cannot see where the
 * size went at all.
 *
 * Kept in the reader's own vocabulary. A trader says "where the size went" and "whether it's
 * holding"; nobody says "delivery window" out loud.
 */
/**
 * The options tape, said once.
 *
 * Flow and Ticker Research are the same feed at different scopes, so they get the same sentence.
 * Writing two would only be writing one of them worse.
 */
const DELAYED_TAPE_ASK =
  "Every print here is 30 minutes old, and dark pool is missing entirely. You're seeing what institutions already did, not what they're doing right now.";

const UPGRADE_LINES: Record<MarketDataSurface, string> = {
  flow: DELAYED_TAPE_ASK,
  research: DELAYED_TAPE_ASK,
  gex: "The flow hitting these walls is 30 minutes old, and dark pool is locked. Knowing where a wall is doesn't tell you whether it's holding.",
};

export const upgradeLine = (surface: MarketDataSurface): string => UPGRADE_LINES[surface];

export const LIVE_TAPE_LINE = "You're on the live feed. Every side, every print, every level.";

/**
 * The button, and it answers the chip directly above it.
 *
 * Two rows read "Locked" and "Delayed"; the button says "Unlock". That pairing does more work than
 * a longer sentence would, and "unlock" is already the app's word for crossing this line.
 */
export const CTA_LABEL = 'Unlock live data';

/** Tooltip on a live spot price, so the dot beside a number reads the same as the dot in the header. */
export const SPOT_DOT_TITLE: Record<MarketSessionPhase, string> = {
  regular: 'Live price',
  pre: 'Live price, pre-market',
  after: 'Live price, after-hours',
  closed: 'Closing price',
};
